import { Injectable } from '@angular/core';
import { FormGroup, FormArray } from '@angular/forms';
import {
    LifecycleRuleset,
    LifecycleRuleGroup,
} from '@r1-compass-library/models/lifecycle-rules/lifecycle-ruleset.model';
import {
    LifecycleRulesDefinitionForm,
    LifecycleRulesRecordSelectionForm,
} from '@r1-compass-library/models/lifecycle-rules/lifecycle-rules-form.model';
import { lookupComparators } from '@r1-compass-library/models/content-library/content-library.model';
import {
    ValidationService,
    ValidationStatus,
    LifecycleRulesSectionName,
} from '@r1-compass-library/features/validation-sync';
import { typeValidator } from '@r1-compass-library/validators';
import { GenericSelectionCriterionForm } from '@r1-compass-library/models/criteria/selection-criteria-form.model';
import { RuleCriterionData } from '@r1-compass-library/models';
import { DisableValue1AndValue2Conditionally } from '@r1-compass-library/features/validation-sync/disable-value1-and-value2-conditionally.service';

export type LifecycleRulesSectionFormMap = {
    definition: FormGroup<LifecycleRulesDefinitionForm>;
    recordSelection:
        | FormGroup<LifecycleRulesRecordSelectionForm>
        | FormArray<FormGroup<LifecycleRulesRecordSelectionForm>>;
};

@Injectable({
    providedIn: 'root',
})
export class LifecycleRulesValidationService
    implements ValidationService<LifecycleRulesSectionName, LifecycleRuleset, LifecycleRulesSectionFormMap>
{
    validateModel(lifecycleRuleset: LifecycleRuleset): ValidationStatus<LifecycleRulesSectionName> {
        const validationStatus: ValidationStatus<LifecycleRulesSectionName> = { erroredSections: new Set() };

        if (this.isDefinitionInvalid(lifecycleRuleset)) {
            validationStatus.erroredSections.add('definition');
        }
        if (this.isRecordSelectionInvalid(lifecycleRuleset.lifecycleRules)) {
            validationStatus.erroredSections.add('recordSelection');
        }
        return validationStatus;
    }

    validateSection<K extends LifecycleRulesSectionName>(
        form: LifecycleRulesSectionFormMap[K],
        section: K
    ): ValidationStatus<LifecycleRulesSectionName> {
        const validationStatus: ValidationStatus<LifecycleRulesSectionName> = { erroredSections: new Set() };
        if (form.invalid) {
            validationStatus.erroredSections.add(section);
        }
        return validationStatus;
    }

    private isDefinitionInvalid(lifecycleRuleset: LifecycleRuleset): boolean {
        const definitionForm = new FormGroup<LifecycleRulesDefinitionForm>(
            new LifecycleRulesDefinitionForm(lifecycleRuleset)
        );

        return definitionForm.invalid;
    }

    private isRecordSelectionInvalid(lifecycleRules: LifecycleRuleGroup[]): boolean {
        const rulesForm = this.buildRecordSelectionForm(lifecycleRules);
        return rulesForm.invalid;
    }

    private buildRecordSelectionForm(
        lifecycleRules: LifecycleRuleGroup[]
    ): FormGroup<LifecycleRulesRecordSelectionForm> {
        const recordSelectionForm = new FormGroup<LifecycleRulesRecordSelectionForm>(
            new LifecycleRulesRecordSelectionForm(lifecycleRules)
        );

        lifecycleRules.forEach(ruleGroup => {
            const criteria =
                ruleGroup.type === 'Create'
                    ? recordSelectionForm.controls.creationRules.controls
                    : recordSelectionForm.controls.updateRules.controls;

            ruleGroup.ruleCriteria.forEach((ruleData, index) => {
                const selectionCriterionForm = criteria[index];
                DisableValue1AndValue2Conditionally.disableLifecycleRules(ruleData, selectionCriterionForm);
                this.setValidatorsForInputTypes(ruleData, selectionCriterionForm);
            });
        });

        return recordSelectionForm;
    }

    /**
     * Sets input type validators (ex: numeric, data) on the formcontrols Value1 and Value2
     */
    private setValidatorsForInputTypes(
        ruleData: RuleCriterionData,
        selectionCriterionForm: FormGroup<GenericSelectionCriterionForm>
    ) {
        const dataMartField = ruleData.dataMartField;
        const comparator = selectionCriterionForm.controls.comparator.value;
        if (dataMartField) {
            const comparatorType = lookupComparators(dataMartField).find(
                comparatorMetadata => comparatorMetadata.value === comparator
            )?.valueType;

            const dataTypeIsNumeric = ['integer', 'number', 'numeric'].includes(dataMartField.dataType.toLowerCase());

            if (comparatorType === 'date') {
                selectionCriterionForm.controls.value1.controls.value.addValidators(typeValidator('date'));
            } else if (comparatorType === 'timeInterval') {
                selectionCriterionForm.controls.value1.controls.value.addValidators(typeValidator('numeric'));
            } else if (comparatorType === 'date&date') {
                selectionCriterionForm.controls.value1.controls.value.addValidators(typeValidator('date'));
                selectionCriterionForm.controls.value2.controls.value.addValidators(typeValidator('date'));
            } else if (comparatorType === 'text' && dataTypeIsNumeric) {
                selectionCriterionForm.controls.value1.controls.value.addValidators(typeValidator('numeric'));
            } else if (comparatorType === 'text&text' && dataTypeIsNumeric) {
                selectionCriterionForm.controls.value1.controls.value.addValidators(typeValidator('numeric'));
                selectionCriterionForm.controls.value2.controls.value.addValidators(typeValidator('numeric'));
            }
            selectionCriterionForm.controls.value1.controls.value.updateValueAndValidity();
            selectionCriterionForm.controls.value2.controls.value.updateValueAndValidity();
        }
    }
}
